// ---------------------------------------------------------------------------
// themeBootCache — a metade em TypeScript do fix de flash de tema.
//
// A outra metade é `public/theme-boot.js`, que roda no `<head>` antes do React
// e LÊ o que este módulo escreve. Os dois precisam concordar sobre a chave e
// sobre o que é um id válido; `tests/unit/themeBootParity.test.ts` lê o arquivo
// público de verdade e falha se divergirem. Duas fontes da mesma regra é como
// `themeId` e `themePreference` se separaram (BUG-80) — aqui a duplicação é
// inevitável (um `.js` estático não importa um módulo do bundle), então o que
// resta é travá-la por teste.
//
// O que se grava é o tema JÁ RESOLVIDO pelos guards (`resolveEquippedThemeId`),
// nunca `themePreference` cru — ver o cabeçalho de `public/theme-boot.js`.
// ---------------------------------------------------------------------------

/** Compartilhada com `public/theme-boot.js`. Mudou aqui, mude lá. */
export const THEME_BOOT_STORAGE_KEY = "livra:boot-theme";

/** Mesma regra do script público: minúsculas, dígitos, `_` e `-`, até 32. */
const VALID_THEME_ID = /^[a-z0-9_-]{1,32}$/;

/**
 * Grava o tema a aplicar na próxima abertura.
 *
 * `default` **apaga** a chave em vez de gravar a string: o script trata os dois
 * como "não aplique nada", e um único estado para uma única semântica evita
 * que alguém leia `"default"` como "o usuário escolheu o Linho" — ele também é
 * o valor de queda de todo guard reprovado.
 */
export function writeBootTheme(themeId: string): void {
  try {
    if (!themeId || themeId === "default" || !VALID_THEME_ID.test(themeId)) {
      window.localStorage.removeItem(THEME_BOOT_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(THEME_BOOT_STORAGE_KEY, themeId);
  } catch {
    // Modo privativo / storage bloqueado. O app inteiro funciona sem isto —
    // o que se perde é a otimização, então falhar em silêncio é o correto.
  }
}

/**
 * Esquece o tema gravado. Chamado quando não há sessão.
 *
 * ⚠️ Não é higiene opcional: a `/` é pública e Linho por definição. Sem esta
 * limpeza, quem desloga faz a landing abrir no tema escuro do último usuário e
 * clarear quando o React monta — o mesmo flash que este fix existe para
 * remover, movido para a página de aquisição.
 */
export function clearBootTheme(): void {
  try {
    window.localStorage.removeItem(THEME_BOOT_STORAGE_KEY);
    // Os tokens saem JUNTO. Deixá-los para trás faria a landing abrir pintada
    // com o tema do último usuário mesmo depois de o id ter sido esquecido —
    // o mesmo flash que este módulo existe para remover, só que pior, porque a
    // pintura sobreviveria ao logout.
    window.localStorage.removeItem(THEME_BOOT_TOKENS_KEY);
  } catch {
    // idem writeBootTheme
  }
}

/**
 * Chave dos TOKENS do tema de boot — separada da do id de propósito.
 *
 * ⚠️ A separação não é organização, é redução de risco. A chave do id já está
 * em produção e é o que faz o anti-flash dos 26 temas do código funcionar;
 * mudar o FORMATO dela (de string para JSON) quebraria o boot de todo mundo que
 * abrisse o app antes de a nova versão gravar. Chave nova é puramente aditiva:
 * quem não tem tokens segue exatamente como hoje.
 *
 * Compartilhada com `public/theme-boot.js`. Mudou aqui, mude lá.
 */
export const THEME_BOOT_TOKENS_KEY = "livra:boot-theme-tokens";

/**
 * Semeia os tokens do tema da PRÓXIMA abertura.
 *
 * Só tema criado no painel precisa disto: os 26 do código têm bloco CSS no
 * bundle e o `data-theme` sozinho já os pinta. Passar `null` limpa.
 *
 * O `id` viaja junto e é conferido na leitura: sem ele, trocar de tema deixaria
 * os tokens do anterior aplicados sobre o `data-theme` do novo, que é uma
 * mistura que não existe em lugar nenhum do catálogo.
 */
export function writeBootTokens(themeId: string, tokens: Record<string, string> | null): void {
  try {
    if (!tokens || !VALID_THEME_ID.test(themeId)) {
      window.localStorage.removeItem(THEME_BOOT_TOKENS_KEY);
      return;
    }
    window.localStorage.setItem(THEME_BOOT_TOKENS_KEY, JSON.stringify({ id: themeId, tokens }));
  } catch {
    // idem writeBootTheme: perde-se a otimização, não a correção.
  }
}

/**
 * Flag de SESSÃO do próximo boot (BUG-211).
 *
 * Compartilhada com `public/theme-boot.js`. Mudou aqui, mude lá.
 *
 * O script lê esta chave no `<head>` e marca `<html data-boot-session>`, que
 * esconde a landing pré-renderizada do `index.html` antes do primeiro paint.
 * Sem ela, quem tem conta via a landing em texto cru piscar em toda abertura
 * até o React montar — o `RootEntry` nunca mostra a landing a quem tem sessão,
 * mas o HTML servido é o mesmo para todo mundo.
 *
 * O valor é `"1"` ou ausência: um estado por semântica, como o tema. Não
 * carrega uid nem nada que identifique a pessoa — só "há sessão".
 */
export const BOOT_SESSION_STORAGE_KEY = "livra:boot-session";

/**
 * Grava (ou apaga) a flag de sessão. Mesmo ciclo do tema no `ThemeContext`:
 * sessão resolvida com usuário → grava; sem usuário → apaga. `false` APAGA em
 * vez de gravar `"0"`, pela mesma razão do `default` do tema.
 *
 * ⚠️ Apagar no logout não é higiene: a `/` sem sessão É a landing, e com a
 * flag esquecida ela abriria escondida até o React montar — um buraco no
 * lugar do flash, na página de aquisição.
 */
export function writeBootSession(hasSession: boolean): void {
  try {
    if (!hasSession) {
      window.localStorage.removeItem(BOOT_SESSION_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(BOOT_SESSION_STORAGE_KEY, "1");
  } catch {
    // idem writeBootTheme: perde-se a otimização, não a correção.
  }
}

/** Só para teste e diagnóstico. */
export function readBootSession(): boolean {
  try {
    return window.localStorage.getItem(BOOT_SESSION_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

/** Só para teste e diagnóstico — o caminho de produção quem lê é o script. */
export function readBootTheme(): string | null {
  try {
    return window.localStorage.getItem(THEME_BOOT_STORAGE_KEY);
  } catch {
    return null;
  }
}
